import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { client } from './lib/apolloClient';
import { AuthProvider } from './context/AuthContext';
import { useIsMobile } from './hooks/useIsMobile';

// Desktop Pages — each becomes its own JS chunk
const Home                 = lazy(() => import('./pages/Home'));
const Login                = lazy(() => import('./pages/Login'));
const Register             = lazy(() => import('./pages/Register'));
const ForgotPassword       = lazy(() => import('./pages/ForgotPassword'));
const Page                 = lazy(() => import('./pages/Page'));
const Category             = lazy(() => import('./pages/Category'));
const Dashboard            = lazy(() => import('./pages/Dashboard'));
const PostAd               = lazy(() => import('./pages/PostAd'));
const MyAds                = lazy(() => import('./pages/MyAds'));
const Favorites            = lazy(() => import('./pages/Favorites'));
const Profile              = lazy(() => import('./pages/Profile'));
const PublicProfile        = lazy(() => import('./pages/PublicProfile'));
const AdDetail             = lazy(() => import('./pages/AdDetail'));
const Search               = lazy(() => import('./pages/Search'));
const Boost                = lazy(() => import('./pages/Boost'));
const BoostPayment         = lazy(() => import('./pages/BoostPayment'));
const AdminBoostManagement = lazy(() => import('./pages/AdminBoostManagement'));
const Stories              = lazy(() => import('./pages/Stories'));
const Messages             = lazy(() => import('./pages/Messages'));
const Terms                = lazy(() => import('./pages/Terms'));
const PrivacyPolicy        = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy         = lazy(() => import('./pages/CookiePolicy'));
const Disclaimer           = lazy(() => import('./pages/Disclaimer'));
const Pricing              = lazy(() => import('./pages/Pricing'));
const SafetyTips           = lazy(() => import('./pages/SafetyTips'));
const PostingRules         = lazy(() => import('./pages/PostingRules'));
const FAQ                  = lazy(() => import('./pages/FAQ'));
const ContactUs            = lazy(() => import('./pages/ContactUs'));

// Mobile Pages
const MobileHome       = lazy(() => import('./pages/MobileHome'));
const MobileLogin      = lazy(() => import('./pages/Mobile/MobileLogin'));
const MobileRegister   = lazy(() => import('./pages/Mobile/MobileRegister'));
const MobileCategories = lazy(() => import('./pages/Mobile/MobileCategories'));
const MobileCategory   = lazy(() => import('./pages/Mobile/MobileCategory'));
const MobileDashboard  = lazy(() => import('./pages/Mobile/MobileDashboard'));
const MobileProfile    = lazy(() => import('./pages/Mobile/MobileProfile'));
const MobilePostAd     = lazy(() => import('./pages/Mobile/MobilePostAd'));
const MobileAdDetail   = lazy(() => import('./pages/Mobile/MobileAdDetail'));
const MobileAlerts     = lazy(() => import('./pages/Mobile/MobileAlerts'));
const MobileMenu       = lazy(() => import('./pages/Mobile/MobileMenu'));

const PageLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60vh',
    }}
  >
    <div
      className="spinner-border"
      role="status"
      style={{ width: '2.5rem', height: '2.5rem', color: 'var(--pw-primary, #e85d4c)' }}
    >
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

const MobileRoutes: React.FC = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/"                  element={<MobileHome />} />
      <Route path="/login"             element={<MobileLogin />} />
      <Route path="/register"          element={<MobileRegister />} />
      <Route path="/forgot-password"   element={<MobileLogin />} />
      <Route path="/page/:slug"        element={<MobileHome />} />
      <Route path="/categories"        element={<MobileCategories />} />
      <Route path="/search"            element={<Search />} />
      <Route path="/category/:slug"    element={<MobileCategory />} />
      <Route path="/notifications"     element={<MobileAlerts />} />
      <Route path="/alerts"            element={<MobileAlerts />} />
      <Route path="/messages"          element={<Messages />} />
      <Route path="/menu"              element={<MobileMenu />} />
      <Route path="/dashboard"         element={<MobileDashboard />} />
      <Route path="/profile"           element={<MobileProfile />} />
      <Route path="/profile/:username" element={<PublicProfile />} />
      <Route path="/post-ad"           element={<MobilePostAd />} />
      <Route path="/my-ads"            element={<MobileDashboard />} />
      <Route path="/favorites"         element={<MobileDashboard />} />
      <Route path="/ad/:slug"          element={<MobileAdDetail />} />
      <Route path="/stories"           element={<Stories />} />
      <Route path="/terms"             element={<Terms />} />
      <Route path="/privacy"             element={<PrivacyPolicy />} />
      <Route path="/privacy-policy"    element={<PrivacyPolicy />} />
      <Route path="/cookies"           element={<CookiePolicy />} />
      <Route path="/disclaimer"        element={<Disclaimer />} />
      <Route path="/pricing"           element={<Pricing />} />
      <Route path="/safety-tips"       element={<SafetyTips />} />
      <Route path="/posting-rules"     element={<PostingRules />} />
      <Route path="/faq"               element={<FAQ />} />
      <Route path="/contact-us"        element={<ContactUs />} />
      <Route path="*"                  element={<MobileHome />} />
    </Routes>
  </Suspense>
);

const DesktopRoutes: React.FC = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/"                    element={<Home />} />
      <Route path="/login"               element={<Login />} />
      <Route path="/register"            element={<Register />} />
      <Route path="/forgot-password"     element={<ForgotPassword />} />
      <Route path="/page/:slug"          element={<Page />} />
      <Route path="/search"              element={<Search />} />
      <Route path="/category/:slug"      element={<Category />} />
      <Route path="/dashboard"           element={<Dashboard />} />
      <Route path="/post-ad"             element={<PostAd />} />
      <Route path="/my-ads"              element={<MyAds />} />
      <Route path="/favorites"           element={<Favorites />} />
      <Route path="/profile"             element={<Profile />} />
      <Route path="/profile/:username"   element={<PublicProfile />} />
      <Route path="/ad/:slug"            element={<AdDetail />} />
      <Route path="/boost"               element={<Boost />} />
      <Route path="/boost/payment/:id"   element={<BoostPayment />} />
      <Route path="/admin/boosts"        element={<AdminBoostManagement />} />
      <Route path="/stories"             element={<Stories />} />
      <Route path="/messages"            element={<Messages />} />
      <Route path="/terms"               element={<Terms />} />
      <Route path="/privacy"             element={<PrivacyPolicy />} />
      <Route path="/privacy-policy"     element={<PrivacyPolicy />} />
      <Route path="/cookies"             element={<CookiePolicy />} />
      <Route path="/disclaimer"          element={<Disclaimer />} />
      <Route path="/pricing"             element={<Pricing />} />
      <Route path="/safety-tips"         element={<SafetyTips />} />
      <Route path="/posting-rules"       element={<PostingRules />} />
      <Route path="/faq"                 element={<FAQ />} />
      <Route path="/contact-us"          element={<ContactUs />} />
      <Route path="*"                    element={<Home />} />
    </Routes>
  </Suspense>
);

const AppContent: React.FC = () => {
  const isMobile = useIsMobile();

  return (
    <Router>
      {isMobile ? <MobileRoutes /> : <DesktopRoutes />}
    </Router>
  );
};

const App: React.FC = () => (
  <ApolloProvider client={client}>
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </ApolloProvider>
);

export default App;
